import { useEffect, useRef, useState } from "react";
import { settingSections as mockSettingSections } from "@/mocks/settings";
import { useApi } from "@/hooks/useApi";
import { apiFetch, apiSend } from "@/lib/api";
import { toast } from "@/components/ui/feedback";
import { GENERATION_MODELS, isQwenModel, type GenerationModel, type GenerationConfig } from "@/services/generationOptions";

interface AppVersion {
  id: string;
  versionCode: number;
  versionName: string;
  apkUrl: string;
  sizeBytes: number;
  changelog: string;
  mandatory: boolean;
  createdAt: string;
}

const fmtMB = (b: number) => (b >= 1 << 20 ? `${(b / (1 << 20)).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

// 版本升级:上传新 APK(存 COS)+ 历史版本。App 启动拉 /app/latest-version 比对升级。
function AppVersionSection() {
  const { data: versions, reload } = useApi<AppVersion[]>("/app-versions", []);
  const fileRef = useRef<HTMLInputElement>(null);
  const [versionCode, setVersionCode] = useState("");
  const [versionName, setVersionName] = useState("");
  const [changelog, setChangelog] = useState("");
  const [mandatory, setMandatory] = useState(false);
  const [busy, setBusy] = useState(false);

  // 选文件即按命名规则自动填版本号:文件名里 "build<数字>" → versionCode;
  // 若含 1.2.3 形式 → versionName,否则默认 1.0.<code>。如 sanxia-build70-release.apk。
  const onPickFile = () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    const code = f.name.match(/build[-_]?(\d+)/i)?.[1] ?? "";
    const semver = f.name.match(/v?(\d+\.\d+\.\d+)/i)?.[1] ?? "";
    if (code) setVersionCode(code);
    setVersionName(semver || (code ? `1.0.${code}` : ""));
  };

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast("请选择 APK 文件", "error"); return; }
    if (!/^\d+$/.test(versionCode.trim())) { toast("版本号(versionCode)必须是正整数", "error"); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("versionCode", versionCode.trim());
    fd.append("versionName", versionName.trim());
    fd.append("changelog", changelog.trim());
    fd.append("mandatory", mandatory ? "true" : "false");
    setBusy(true);
    try {
      const res = await apiFetch("/app-versions", { method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      toast("上传成功,已发布新版本", "success");
      setVersionCode(""); setVersionName(""); setChangelog(""); setMandatory(false);
      if (fileRef.current) fileRef.current.value = "";
      reload();
    } catch (e) { toast("上传失败:" + (e as Error).message, "error"); }
    finally { setBusy(false); }
  };

  const del = async (v: AppVersion) => {
    if (!window.confirm(`删除版本 ${v.versionName}(code ${v.versionCode})?`)) return;
    try { await apiSend("DELETE", `/app-versions/${v.id}`); reload(); }
    catch (e) { toast("删除失败:" + (e as Error).message, "error"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-1"><i className="ri-rocket-line mr-1.5 text-teal-600" />发布新版本</h2>
        <p className="text-xs text-gray-400 mb-4">上传 release APK(存腾讯云 COS);App 启动时比对版本号,有新版即提示在线升级。versionCode 必须比当前更大。<br/>命名规则 <code>sanxia-build&lt;版本号&gt;-release.apk</code>(如 sanxia-build71-release.apk),选文件后自动填版本号/版本名,可再手动改。</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs text-gray-600">版本号 versionCode(整数)
            <input value={versionCode} onChange={(e) => setVersionCode(e.target.value)} placeholder="如 70" className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </label>
          <label className="text-xs text-gray-600">版本名 versionName
            <input value={versionName} onChange={(e) => setVersionName(e.target.value)} placeholder="如 1.0.70" className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </label>
        </div>
        <label className="text-xs text-gray-600 block mt-3">更新说明 changelog
          <textarea value={changelog} onChange={(e) => setChangelog(e.target.value)} rows={3} placeholder="本次更新内容…" className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
        </label>
        <div className="flex items-center gap-4 mt-3">
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} />强制更新
          </label>
          <input ref={fileRef} type="file" accept=".apk,application/vnd.android.package-archive" onChange={onPickFile} className="text-xs flex-1" />
        </div>
        <button onClick={upload} disabled={busy} className="mt-4 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60">
          <i className={busy ? "ri-loader-4-line animate-spin mr-1.5" : "ri-upload-2-line mr-1.5"} />{busy ? "上传中…(APK 较大,请稍候)" : "上传并发布"}
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 text-sm text-gray-500">历史版本(共 {versions.length})</div>
        {versions.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">暂无已发布版本</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50"><tr>
              {["版本号", "版本名", "大小", "强制", "发布时间", "操作"].map((h) => (
                <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {versions.map((v, i) => (
                <tr key={v.id} className={i === 0 ? "bg-teal-50/40" : ""}>
                  <td className="px-4 py-2.5 text-sm font-semibold text-gray-900">{v.versionCode}{i === 0 && <span className="ml-1.5 text-[10px] text-teal-600">最新</span>}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-700">{v.versionName}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-500">{fmtMB(v.sizeBytes)}</td>
                  <td className="px-4 py-2.5 text-sm">{v.mandatory ? <span className="text-red-600">强制</span> : <span className="text-gray-400">可选</span>}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-500">{v.createdAt}</td>
                  <td className="px-4 py-2.5 text-sm">
                    <a href={v.apkUrl} target="_blank" rel="noreferrer" className="text-teal-600 mr-3">下载</a>
                    <button onClick={() => del(v)} className="text-red-500">删除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

interface Persona {
  key: string;
  name: string;
  tag: string;
  ttsVoice: string;
  description: string;
  enabled: boolean;
  seq: number;
}

// dashscope qwen-tts built-in voices; free-text input stays possible.
const TTS_VOICES = ["Cherry", "Serena", "Ethan", "Chelsie"];

// 讲解人格与音色 — real config (personas table), drives multi-role track
// generation, TTS preview and the app's三人格 selection.
function ResourcePackSection() {
  const { data: flag, reload } = useApi<{ key: string; value: string }>(
    "/app-flags/resource_check_interval", { key: "resource_check_interval", value: "" });
  const [saving, setSaving] = useState(false);
  const current = flag.value || "180";

  const save = async (v: string) => {
    setSaving(true);
    try {
      await apiSend("PUT", "/app-flags/resource_check_interval", { value: v });
      toast("已保存，App 下次轮询起生效", "success");
      reload();
    } catch (e) { toast(e instanceof Error ? e.message : "保存失败", "error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900"><i className="ri-archive-2-line mr-1.5 text-teal-600" />内容资源包</h2>
          <p className="text-xs text-gray-500 mt-1">
            讲解词 / 语音 / 视频统一打包下发：App 启动后按间隔轮询版本号，内容有变化才增量拉取；导出的 .sxpack 可作为离线安装资源预置进 APK
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-gray-500">检测间隔</label>
          <select value={current} disabled={saving} onChange={(e) => save(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
            <option value="60">1 分钟</option>
            <option value="180">3 分钟</option>
            <option value="300">5 分钟</option>
            <option value="600">10 分钟</option>
            <option value="1800">30 分钟</option>
          </select>
          <a href={`${import.meta.env.VITE_API_BASE || ""}/api/v1/pack/export`} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 whitespace-nowrap">
            <i className="ri-download-2-line mr-1" />导出资源包
          </a>
        </div>
      </div>
    </div>
  );
}

function PersonasSection() {
  const { data: personas, reload } = useApi<Persona[]>("/personas-config", []);
  const [edits, setEdits] = useState<Record<string, Partial<Persona>>>({});

  const val = <K extends keyof Persona>(p: Persona, k: K): Persona[K] =>
    (edits[p.key]?.[k] ?? p[k]) as Persona[K];
  const setVal = (key: string, patch: Partial<Persona>) =>
    setEdits((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const save = async (p: Persona) => {
    const merged = { ...p, ...edits[p.key] };
    try {
      await apiSend("PUT", `/personas-config/${p.key}`, merged);
      toast(`「${merged.name}」已保存`, "success");
      setEdits((prev) => ({ ...prev, [p.key]: {} }));
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "保存失败", "error");
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200">
        <div className="w-9 h-9 flex items-center justify-center bg-amber-100 text-amber-700 rounded-lg">
          <i className="ri-user-voice-line text-lg" />
        </div>
        <div>
          <span className="text-sm font-medium text-gray-900">讲解人格与音色（三人格）</span>
          <p className="text-xs text-gray-400 mt-0.5">音色用于多角色音轨生成与试听；停用的人格不参与批量生成</p>
        </div>
      </div>
      <div className="divide-y divide-gray-100">
        {personas.map((p) => (
          <div key={p.key} className="px-5 py-4 grid grid-cols-1 lg:grid-cols-[8rem_7rem_11rem_1fr_auto_auto] gap-3 items-center">
            <input
              value={val(p, "name")}
              onChange={(e) => setVal(p.key, { name: e.target.value })}
              className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium"
            />
            <input
              value={val(p, "tag")}
              onChange={(e) => setVal(p.key, { tag: e.target.value })}
              placeholder="版本标签"
              className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
            />
            <div className="flex items-center gap-1">
              <select
                value={TTS_VOICES.includes(val(p, "ttsVoice")) ? val(p, "ttsVoice") : "__custom__"}
                onChange={(e) => e.target.value !== "__custom__" && setVal(p.key, { ttsVoice: e.target.value })}
                className="px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
              >
                {TTS_VOICES.map((v) => <option key={v} value={v}>{v}</option>)}
                <option value="__custom__">自定义…</option>
              </select>
              <input
                value={val(p, "ttsVoice")}
                onChange={(e) => setVal(p.key, { ttsVoice: e.target.value })}
                className="w-24 px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono"
                title="dashscope 音色 ID"
              />
            </div>
            <input
              value={val(p, "description")}
              onChange={(e) => setVal(p.key, { description: e.target.value })}
              placeholder="风格描述"
              className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600"
            />
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={val(p, "enabled")}
                onChange={(e) => setVal(p.key, { enabled: e.target.checked })}
                className="accent-teal-600 w-4 h-4"
              />
              启用
            </label>
            <button
              onClick={() => save(p)}
              className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"
            >
              保存
            </button>
          </div>
        ))}
        {personas.length === 0 && <p className="px-5 py-8 text-sm text-gray-400 text-center">加载中…</p>}
      </div>
    </div>
  );
}

interface ProviderCapability {
  name: string;
  auxiliary: boolean;
  styles: string[];
  operations: string[];
  healthy: boolean;
  message?: string;
}

interface ProviderStatusResponse {
  mode: "direct" | "provider";
  ready: boolean;
  message?: string;
  capabilities?: ProviderCapability[];
}

const PROVIDER_STATUS_FALLBACK: ProviderStatusResponse = {
  mode: "direct",
  ready: false,
  message: "",
  capabilities: [],
};

const STYLE_LABELS: Record<string, string> = {
  chibi: "Q版漫画 (chibi)",
  photo: "写实摄影 (photo)",
  real: "真实换脸 (real)",
};

const OPERATION_LABELS: Record<string, string> = {
  faceswap: "换脸合成",
  cutout: "人像抠图",
  analyze: "人脸分析",
  generation: "图像生成",
  segment: "图像分割",
};

function ProviderStatusSection() {
  const { data: status, loading, error, reload } = useApi<ProviderStatusResponse>(
    "/faceswap/provider-status",
    PROVIDER_STATUS_FALLBACK
  );

  const isDirect = status.mode === "direct";
  const isDegraded = !status.ready && (status.capabilities?.some((c) => c.healthy) ?? false);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-5 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-teal-100 text-teal-700 rounded-lg shrink-0">
            <i className="ri-cpu-line text-lg" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-medium text-gray-900">统一算力服务状态 (Provider Status)</h2>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                  status.mode === "provider"
                    ? "bg-teal-50 text-teal-700 border border-teal-200"
                    : "bg-slate-50 text-slate-700 border border-slate-200"
                }`}
              >
                <i className={status.mode === "provider" ? "ri-server-line" : "ri-link-line"} />
                {status.mode === "provider" ? "统一 Provider 模式" : "直连模式 (Direct)"}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              换脸推理、人脸分析与人像抠图计算节点的运行健康度与能力矩阵
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              loading
                ? "bg-gray-100 text-gray-600"
                : error
                ? "bg-rose-50 text-rose-700 border border-rose-200"
                : isDirect
                ? "bg-slate-50 text-slate-700 border border-slate-200"
                : status.ready
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : isDegraded
                ? "bg-amber-50 text-amber-700 border border-amber-200"
                : "bg-rose-50 text-rose-700 border border-rose-200"
            }`}
          >
            <i
              className={
                loading
                  ? "ri-loader-4-line animate-spin"
                  : error
                  ? "ri-close-circle-line"
                  : isDirect
                  ? "ri-link-line"
                  : status.ready
                  ? "ri-checkbox-circle-line"
                  : isDegraded
                  ? "ri-alert-line"
                  : "ri-close-circle-line"
              }
            />
            {loading
              ? "检查中…"
              : error
              ? "连接失败"
              : isDirect
              ? "API 直连模式"
              : status.ready
              ? "算力就绪 (Ready)"
              : isDegraded
              ? "部分降级 (Degraded)"
              : "算力不可用 (Unreachable)"}
          </span>

          <button
            onClick={reload}
            disabled={loading}
            title="刷新算力状态"
            className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
          >
            <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {error && (
          <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-100 rounded-lg text-xs text-rose-700">
            <i className="ri-error-warning-line text-base shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">无法获取算力服务状态</p>
              <p className="mt-0.5 text-rose-600">
                {error.message || "未能成功连接 /api/v1/faceswap/provider-status 端点，请确认服务正常运行。"}
              </p>
            </div>
          </div>
        )}

        {status.message && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
            <i className="ri-information-line text-sm shrink-0 mt-0.5" />
            <span>{status.message}</span>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2.5">
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">计算节点与能力矩阵</h3>
            <span className="text-xs text-gray-400">共 {status.capabilities?.length ?? 0} 个计算后端</span>
          </div>

          {status.capabilities && status.capabilities.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {status.capabilities.map((cap) => (
                <div
                  key={cap.name}
                  className={`p-3.5 rounded-lg border text-xs transition-colors ${
                    cap.healthy
                      ? "bg-gray-50/70 border-gray-200 hover:border-gray-300"
                      : "bg-rose-50/50 border-rose-200"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{cap.name}</span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
                          cap.auxiliary
                            ? "bg-purple-50 text-purple-700 border border-purple-200"
                            : "bg-blue-50 text-blue-700 border border-blue-200"
                        }`}
                      >
                        {cap.auxiliary ? "辅助算力" : "主力算力"}
                      </span>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        cap.healthy
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-rose-50 text-rose-700 border border-rose-200"
                      }`}
                    >
                      <i className={cap.healthy ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"} />
                      {cap.healthy ? "正常" : "异常"}
                    </span>
                  </div>

                  {cap.message && (
                    <p className="text-[11px] text-rose-600 mb-2 p-1.5 bg-rose-100/50 rounded">
                      {cap.message}
                    </p>
                  )}

                  <div className="space-y-1.5 mt-2 pt-2 border-t border-gray-200/80">
                    <div className="flex items-start gap-2">
                      <span className="text-gray-400 shrink-0 w-14">支持风格:</span>
                      <div className="flex flex-wrap gap-1">
                        {cap.styles && cap.styles.length > 0 ? (
                          cap.styles.map((s) => (
                            <span key={s} className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-gray-700 text-[11px]">
                              {STYLE_LABELS[s] || s}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-[11px]">无</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <span className="text-gray-400 shrink-0 w-14">支持算子:</span>
                      <div className="flex flex-wrap gap-1">
                        {cap.operations && cap.operations.length > 0 ? (
                          cap.operations.map((op) => (
                            <span key={op} className="px-1.5 py-0.5 bg-teal-50/80 border border-teal-200 text-teal-700 rounded text-[11px]">
                              {OPERATION_LABELS[op] || op}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-[11px]">无</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-6 text-center text-xs text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
              {loading
                ? "正在加载节点能力明细…"
                : isDirect
                ? "当前由 API 直接连接算力后端；启用统一 Provider 模式后显示节点能力矩阵"
                : "暂无可用计算节点信息"}
            </div>
          )}
        </div>

        <div className="text-[11px] text-gray-400 bg-gray-50/80 p-2.5 rounded-lg border border-gray-100 flex items-start gap-2">
          <i className="ri-shield-check-line text-teal-600 text-xs shrink-0 mt-0.5" />
          <span>
            换脸算力凭证由服务端安全隔离管理（此卡片为只读状态矩阵）；图像生成 DashScope 凭证与参数可在下方万相配置区直接维护。
          </span>
        </div>
      </div>
    </div>
  );
}

interface WanProviderConfig {
  apiUrl?: string;
  hasKey?: boolean;
  maskedKey?: string;
  modelName?: string;
  extra?: Record<string, number | boolean>;
  ready?: boolean;
  credentialSource?: string;
}

interface WanConfig {
  enabled: boolean;
  useCutout: boolean;
  enabledSeasons: string[];
  provider?: WanProviderConfig;
  generation?: GenerationConfig;
}

const WAN_CONFIG_FALLBACK: WanConfig = {
  enabled: false,
  useCutout: true,
  enabledSeasons: ["summer"],
  provider: {
    apiUrl: "https://dashscope.aliyuncs.com/api/v1/services/aigc/image-generation/generation",
    hasKey: false,
    maskedKey: "",
    modelName: "wan2.7-image",
    extra: {
      width: 1024,
      height: 1024,
      timeout_seconds: 120,
      max_concurrent_submissions: 3,
      min_submission_interval_seconds: 2,
      max_poll_concurrent: 4,
    },
    ready: false,
    credentialSource: "",
  },
};

function WanConfigSection() {
  const { data: config, dataPath, error: configError, loading, reload } = useApi<WanConfig>("/faceswap/wan-config", WAN_CONFIG_FALLBACK);
  const configuredPromptExtend = typeof config.provider?.extra?.prompt_extend === "boolean"
    ? config.provider.extra.prompt_extend
    : true;
  const [enabled, setEnabled] = useState(config.enabled);
  const [useCutout, setUseCutout] = useState(config.useCutout ?? true);
  const [seasons, setSeasons] = useState<string[]>(config.enabledSeasons);

  // Provider fields
  const [apiUrl, setApiUrl] = useState(config.provider?.apiUrl ?? "");
  const [modelName, setModelName] = useState<GenerationModel>(config.generation?.default.model ?? (config.provider?.modelName as GenerationModel || "wan2.7-image"));
  const [promptExtend, setPromptExtend] = useState(configuredPromptExtend);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(config.enabled);
    setUseCutout(config.useCutout ?? true);
    setSeasons(config.enabledSeasons || []);
    setApiUrl(config.provider?.apiUrl ?? "");
    setModelName(config.generation?.default.model ?? (config.provider?.modelName as GenerationModel || "wan2.7-image"));
    setPromptExtend(configuredPromptExtend);
    setApiKey("");
  }, [config, configuredPromptExtend]);

  const toggleSeason = (season: string) => {
    setSeasons((current) =>
      current.includes(season) ? current.filter((item) => item !== season) : [...current, season]
    );
  };

  const hasPromptExtendEdits = promptExtend !== configuredPromptExtend;
  const hasProviderEdits =
    apiUrl.trim() !== (config.provider?.apiUrl ?? "") ||
    modelName !== (config.generation?.default.model ?? config.provider?.modelName ?? "wan2.7-image") ||
    apiKey.trim() !== "" ||
    hasPromptExtendEdits;

  const save = async () => {
    if (dataPath !== "/faceswap/wan-config") return;
    if (enabled && seasons.length === 0) {
      toast("至少保留一个可选季节", "error");
      return;
    }

    setSaving(true);
    try {
      const payload: {
        enabled: boolean;
        useCutout: boolean;
        enabledSeasons: string[];
        provider?: {
          apiUrl?: string;
          apiKey?: string;
          modelName?: string;
          extra?: Record<string, number | boolean>;
        };
      } = {
        enabled,
        useCutout,
        enabledSeasons: seasons,
      };

      if (hasProviderEdits) {
        const prov: {
          apiUrl?: string;
          apiKey?: string;
          modelName?: string;
          extra?: Record<string, number | boolean>;
        } = {};

        if (apiUrl.trim()) prov.apiUrl = apiUrl.trim();
        prov.modelName = modelName;
        if (apiKey.trim()) prov.apiKey = apiKey.trim();
        if (hasPromptExtendEdits) prov.extra = { prompt_extend: promptExtend };

        payload.provider = prov;
      }

      await apiSend("PUT", "/faceswap/wan-config", payload);
      toast("万相生成与 Provider 配置已保存", "success");
      setApiKey("");
      reload();
    } catch (error) {
      toast("保存失败：" + (error instanceof Error ? error.message : String(error)), "error");
    } finally {
      setSaving(false);
    }
  };

  const isReady = config.provider?.ready ?? false;
  const hasKey = config.provider?.hasKey ?? false;
  const maskedKey = config.provider?.maskedKey || "";
  const credSource = config.provider?.credentialSource || "";

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-5 py-4 border-b border-gray-200">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-medium text-gray-900">万相服装换脸与生成配置</h2>
            {credSource && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                来源: {credSource === "database" ? "数据库持久化" : credSource === "env" ? "环境变量" : credSource}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            独立使用 DashScope 图像生成 Provider，不影响视觉识别与通用换脸配置。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              isReady
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-amber-50 text-amber-700 border border-amber-200"
            }`}
          >
            <i className={isReady ? "ri-checkbox-circle-line" : "ri-alert-line"} />
            {isReady ? "图像生成 Provider 已就绪" : "图像生成 Provider 未就绪"}
          </span>
        </div>
      </div>

      <div className="px-5 py-5 space-y-6">
        {configError && <p role="alert" className="text-sm text-red-600">配置加载失败，未获取服务端配置前不能保存。<button type="button" onClick={reload} className="ml-2 underline">重新加载</button></p>}
        {/* Business toggles */}
        <div className="space-y-4">
          <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">业务策略与季节生效</h3>
          <div className="flex items-center justify-between gap-4 p-3.5 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-800">启用万相生成</p>
              <p className="text-xs text-gray-500 mt-0.5">关闭时不会创建万相时装任务，原有换脸流程保持不变。</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600" />
            </label>
          </div>

          <div className="flex items-center justify-between gap-4 p-3.5 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <label htmlFor="wan-use-cutout" className="text-sm font-medium text-gray-800">使用抠像后的图作为参考图</label>
              <p className="text-xs text-gray-500 mt-0.5">开启时先尝试抠像，失败时沿用原图生成最终照片；关闭时跳过抠像，直接使用保留的原自拍。仅影响原三峡业务后续新任务，已有任务保持原策略，Photo 业务不受影响。</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0">
              <input
                id="wan-use-cutout"
                type="checkbox"
                role="switch"
                checked={useCutout}
                onChange={(event) => setUseCutout(event.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600" />
            </label>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-800">启用季节</p>
            <p className="text-xs text-gray-500 mt-0.5">仅为勾选的季节创建万相时装任务。</p>
            <div className="flex flex-wrap gap-4 mt-2.5">
              {([["spring_autumn", "春秋"], ["summer", "夏"], ["winter", "冬"]] as const).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={seasons.includes(value)}
                    onChange={() => toggleSeason(value)}
                    className="w-4 h-4 accent-teal-600 rounded"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Provider settings */}
        <div className="space-y-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">DashScope / Wan 服务端点与凭证</h3>
            <span className="text-xs text-gray-400">
              {hasKey ? (
                <span className="text-emerald-600 font-mono">密钥: {maskedKey || "已配置"}</span>
              ) : (
                <span className="text-amber-600">未配置 API 密钥</span>
              )}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                API 服务端点 (API URL)
              </label>
              <input
                type="text"
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                placeholder="https://dashscope.aliyuncs.com/api/v1/services/aigc/image-generation/generation"
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
              <p className="text-xs text-gray-400 mt-1">Wan 图像生成服务提交端点</p>
            </div>

            <div>
              <label htmlFor="wan-default-model" className="block text-xs font-medium text-gray-700 mb-1.5">
                默认生成模型 (Model Name)
              </label>
              <select
                id="wan-default-model"
                value={modelName}
                onChange={(e) => setModelName(e.target.value as GenerationModel)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              >
                {GENERATION_MODELS.map((model) => <option key={model} value={model}>{model}</option>)}
              </select>
              <p className="text-xs text-gray-400 mt-1">只影响新建最终照片任务；已有任务保持冻结配置。Qwen 提示词改写默认值可在下方设置，人工重生成可单独调整。</p>
            </div>

            {isQwenModel(modelName) && (
              <div className="md:col-span-2 flex items-center justify-between gap-4 p-3.5 bg-gray-50 rounded-lg border border-gray-100">
                <div>
                  <label htmlFor="wan-prompt-extend" className="text-sm font-medium text-gray-800">提示词改写</label>
                  <p className="text-xs text-gray-500 mt-0.5">作为新建 Qwen 最终照片任务的默认值；切换到 Wan 时不启用改写，但保留此偏好。</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    id="wan-prompt-extend"
                    type="checkbox"
                    checked={promptExtend}
                    onChange={(event) => setPromptExtend(event.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600" />
                </label>
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                API 密钥 (API Key 轮换)
              </label>
              <div className="relative">
                <input
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={
                    hasKey
                      ? `已配置（${maskedKey || "******"}），留空保存将保持当前存储的密钥`
                      : "未配置密钥，请输入 DashScope API Key"
                  }
                  className="w-full px-3 py-2 pr-10 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  <i className={showKey ? "ri-eye-off-line" : "ri-eye-line"} />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {hasKey
                  ? "安全提示：当前已有有效凭证。留空保存不会覆盖原密钥；仅在需要轮转时输入新 Key。"
                  : "请输入阿里云 DashScope API Key (sk-...) 以启用万相生成。"}
              </p>
            </div>
          </div>
        </div>

        {/* Action */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <span className="text-xs text-gray-400">
            {hasProviderEdits ? "检测到 Provider 算力配置已修改，保存时将一并更新" : "当前未修改 Provider 算力配置"}
          </span>
          <button
            onClick={save}
            disabled={saving || loading || dataPath !== "/faceswap/wan-config"}
            className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60 transition-colors flex items-center gap-2"
          >
            {saving && <i className="ri-loader-4-line animate-spin" />}
            {saving ? "保存中..." : "保存配置"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SettingsPage() {
  const { data: settingSections } = useApi<typeof mockSettingSections>(
    "/data/settingSections",
    mockSettingSections,
  );

  const [activeTab, setActiveTab] = useState<string>("personas");
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  const handleChange = (sectionId: string, label: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [`${sectionId}-${label}`]: value }));
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">系统配置</h1>
        <p className="text-sm text-gray-500 mt-1">服务凭证、租户、权限、语言等系统设置</p>
      </div>

      {/* Tab 栏:每个配置区一个标签,点哪看哪,免去长页滚动 */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200">
        {[
          { id: "wanFaceswap", title: "万相服装换脸", icon: "ri-t-shirt-2-line" },
          { id: "personas", title: "讲解人格", icon: "ri-user-voice-line" },
          { id: "resourcePack", title: "内容资源包", icon: "ri-archive-2-line" },
          { id: "appVersion", title: "版本升级", icon: "ri-rocket-line" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              activeTab === t.id ? "border-teal-600 text-teal-700" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className={t.icon} />
            {t.title}
          </button>
        ))}
      </div>

      {activeTab === "personas" && <PersonasSection />}
      {activeTab === "resourcePack" && <ResourcePackSection />}
      {activeTab === "appVersion" && <AppVersionSection />}
      {activeTab === "wanFaceswap" && (
        <div className="space-y-5">
          <ProviderStatusSection />
          <WanConfigSection />
        </div>
      )}

      <div className="space-y-4">
        {settingSections.filter((section) => section.id === activeTab).map((section) => (
          <div key={section.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4">
              <div className="w-9 h-9 flex items-center justify-center bg-teal-100 text-teal-700 rounded-lg">
                <i className={`${section.icon} text-lg`} />
              </div>
              <span className="text-sm font-medium text-gray-900">{section.title}</span>
            </div>
            {(
              <div className="px-5 pb-5 border-t border-gray-200">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-4">
                  {section.items.map((item) => (
                    <div key={item.label}>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{item.label}</label>
                      {item.type === "select" ? (
                        <select
                          value={formValues[`${section.id}-${item.label}`] || item.value}
                          onChange={(e) => handleChange(section.id, item.label, e.target.value)}
                          className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                        >
                          <option value={item.value}>{item.value}</option>
                          <option value="other">其他选项...</option>
                        </select>
                      ) : item.type === "toggle" ? (
                        <div className="flex items-center gap-3">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formValues[`${section.id}-${item.label}`] !== "关闭" && item.value !== "关闭"}
                              className="sr-only peer"
                              onChange={(e) => handleChange(section.id, item.label, e.target.checked ? "开启" : "关闭")}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600" />
                          </label>
                          <span className="text-sm text-gray-700">
                            {formValues[`${section.id}-${item.label}`] || item.value}
                          </span>
                        </div>
                      ) : (
                        <input
                          type={item.type === "password" ? "password" : "text"}
                          value={formValues[`${section.id}-${item.label}`] || item.value}
                          onChange={(e) => handleChange(section.id, item.label, e.target.value)}
                          className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                        />
                      )}
                      {item.description && (
                        <p className="text-xs text-gray-400 mt-1">{item.description}</p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
                  <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
                    重置
                  </button>
                  <button className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors">
                    保存配置
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default SettingsPage;